import { useTranslate } from '@ur-solutions/react-hooks'
import { parseISO } from 'date-fns'
import React from 'react'
import styled from 'styled-components'
import { format } from '@/utils/date-fns'
import { unlessLoadingDots } from '@/utils/loading'
import { Button, TextArea } from '@/components_deprecated'
import { Icon } from '@ur-solutions/react-components'

const Wrapper = styled.div`
  grid-area: details;

  display: grid;
  grid-template-columns: 1fr auto auto auto;
  grid-template-areas:
    'user user user legalBasis'
    'reason reason reason reasonValue'
    'archivedBasis archivedBasis archivedBasis archivedAt'
    'solution solution solution solution'
    'leaderComment leaderComment leaderComment leaderComment';

  background: ${props => props.theme.colors.quaternary};

  ${props => props.theme.media.mobile} {
    overflow-x: auto;

    display: flex;
    flex-direction: column;
  }
`
interface BoxProps {
  area: string
  borderTop?: boolean
  borderRight?: boolean
  borderBottom?: boolean
  borderLeft?: boolean
}
export const Box = styled.div<BoxProps>`
  grid-area: ${props => props.area};

  display: flex;
  flex-direction: column;
  padding: 1.5rem 2rem;

  border: 0px solid ${props => props.theme.colors.gray8};
  border-top-width: ${props => props.borderTop && '1px'};
  border-right-width: ${props => props.borderRight && '1px'};
  border-bottom-width: ${props => props.borderBottom && '1px'};
  border-left-width: ${props => props.borderLeft && '1px'};

  h4 {
    margin: 0 0 0.3rem 0;
    color: ${props => props.theme.colors.gray5};
    font-size: 0.9rem;
    font-weight: 600;
  }
  span {
    font-weight: 600;
    font-size: 1.2rem;
  }

  ${props => props.theme.media.mobile} {
    padding: 1rem;

    h4 {
      margin: 0 0 0.5rem 0;
      font-size: 0.7rem;
    }
    span {
      font-size: 0.9rem;
    }
    border-bottom: 1px solid ${props => props.theme.colors.gray8};
    border-right: 0;
    border-left: 0;
    border-top: 0;
  }
`

interface DetailsProps {
  loading: boolean

  reason: string | undefined
  reasonLowerLimit: string | null | undefined
  reasonValue: string | null | undefined
  reasonUpperLimit: string | null | undefined
  userFullname: string | undefined
  archivedAt: string | null | undefined
  archivedBasis: string | null | undefined
  legalBasis: string[] | undefined
  solution?: string | null
  leadersComment?: string | null

  editing?: boolean
  canEdit?: boolean

  onStartEditing?: () => void
  onSubmitLeadersComment?: () => void
  onEditLeadersComment?: (value: string) => void
}

export const Details: React.FC<DetailsProps> = ({
  loading,

  reason,
  // TODO unused?
  reasonLowerLimit: _reasonLowerLimit,
  reasonUpperLimit: _reasonUpperLimit,
  reasonValue,
  userFullname,
  archivedAt,
  archivedBasis,
  legalBasis,
  solution,
  leadersComment,

  editing,
  canEdit,

  onStartEditing,
  onSubmitLeadersComment,
  onEditLeadersComment,
}) => {
  const translations = useTranslate({
    unknown: 'common.unknown',

    edit: 'common.edit',
    save: 'common.save',

    explaination: 'common.explaination',
    commentFromLeader: 'infringements.comment-from-leader',

    driver: 'common.driver',
    basis: 'common.basis',
    value: 'common.value',
    article: 'common.article',

    archivedBasis: 'infringements.archived-basis',
    archivedDate: 'infringements.archived-date',

    reasonLowerLimit: 'infringements.lower-limit',
    reasonUpperLimit: 'infringements.upper-limit',
    legalBasis: 'infringements.legal-basis',
    nHours: ['common.n-hours', { n: '' }],
    nMinutes: ['common.n-minutes', { n: '' }],
  })
  const unlessLoading = unlessLoadingDots(loading, translations.unknown)

  const isArchived = !!archivedAt

  const archivedDate = archivedAt ? parseISO(archivedAt) : null
  const archivedDateText =
    archivedDate !== null ? format(archivedDate, 'HH:mm dd.MM.yyyy') : null

  const articleText =
    legalBasis?.length === 0
      ? '-'
      : translations.article + ': ' + legalBasis?.join(', ')

  return (
    <Wrapper>
      <Box area="user">
        <h4>{translations.driver}</h4>
        <span>{unlessLoading(userFullname || '-')}</span>
      </Box>
      <Box area="legalBasis">
        <h4>{translations.legalBasis}</h4>
        <span>{unlessLoading(articleText || '-')}</span>
      </Box>

      <Box area="reason" borderTop>
        <h4>{translations.basis}</h4>
        <span>{unlessLoading(reason || '-')}</span>
      </Box>

      {reasonValue && (
        <>
          <Box area="reasonValue" borderTop borderLeft>
            <h4>{translations.value}</h4>
            <span>{unlessLoading(reasonValue)}</span>
          </Box>
        </>
      )}

      {isArchived && (
        <>
          <Box area="archivedBasis" borderTop>
            <h4>{translations.archivedBasis}</h4>
            <span>{unlessLoading(archivedBasis)}</span>
          </Box>
          <Box area="archivedAt" borderTop borderLeft>
            <h4>{translations.archivedDate}</h4>
            <span>{unlessLoading(archivedDateText)}</span>
          </Box>
        </>
      )}
      <Box area="solution" borderTop>
        <h4>{translations.explaination}</h4>
        <span>{unlessLoading(solution || '-')}</span>
      </Box>
      <Box area="leaderComment" borderTop>
        <h4>{translations.commentFromLeader}</h4>
        {editing && canEdit && onEditLeadersComment && onStartEditing ? (
          <TextArea
            value={leadersComment || ''}
            onChange={value => onEditLeadersComment(value)}
            onFocus={onStartEditing}
            fullWidth
            background="white"
            className="mb-2"
          />
        ) : (
          <>
            <span className="mb-2">{unlessLoading(leadersComment || '-')}</span>
          </>
        )}
        {canEdit && !editing && onStartEditing && (
          <Button
            height="2rem"
            padding="0 2rem"
            width="100px"
            onClick={onStartEditing}
          >
            <Icon icon="edit" fixedWidth margin="0 6px 0 0" />
            {translations.edit}
          </Button>
        )}
        {canEdit && editing && onSubmitLeadersComment && (
          <Button
            height="2rem"
            padding="0 2rem"
            width="100px"
            onClick={onSubmitLeadersComment}
          >
            <Icon icon="check" fixedWidth margin="0 6px 0 0" />
            {translations.save}
          </Button>
        )}
      </Box>
    </Wrapper>
  )
}
